import { z } from 'zod';
import { AuditFieldsSchema, MoneyHufSchema, TenantIdSchema } from '../types/common.types';
import { PaginationQuerySchema } from '../types/pagination.types';

export const RentalStatusSchema = z.enum([
  'PENDING',
  'CONFIRMED',
  'ACTIVE',
  'RETURNED',
  'CANCELLED',
  'OVERDUE',
]);
export type RentalStatus = z.infer<typeof RentalStatusSchema>;

export const BookingSchema = z
  .object({
    id: z.string().uuid(),
    tenantId: TenantIdSchema,
    portalUserId: z.string().uuid(),
    partnerId: z.string().uuid().nullable(),
    equipmentTypeId: z.string().uuid(),
    equipmentUnitId: z.string().uuid().nullable(),
    startDate: z.string().date(),
    endDate: z.string().date(),
    days: z.number().int().positive(),
    status: RentalStatusSchema,
    estimatedTotal: MoneyHufSchema,
    depositRequired: MoneyHufSchema.nullable(),
    notes: z.string().nullable(),
  })
  .merge(AuditFieldsSchema);
export type Booking = z.infer<typeof BookingSchema>;

export const BookingCreateSchema = BookingSchema.pick({
  equipmentTypeId: true,
  startDate: true,
  endDate: true,
  notes: true,
});
export type BookingCreate = z.infer<typeof BookingCreateSchema>;

export const PortalBookingListQuerySchema = PaginationQuerySchema.extend({
  status: RentalStatusSchema.optional(),
  fromDate: z.string().date().optional(),
  toDate: z.string().date().optional(),
});
export type PortalBookingListQuery = z.infer<typeof PortalBookingListQuerySchema>;

export const RentalExtendRequestSchema = z.object({
  newEndDate: z.string().date(),
  reason: z.string().max(500),
});
export type RentalExtendRequest = z.infer<typeof RentalExtendRequestSchema>;

// ──────────────────────────────────────────────────────────────────────────
// Multi-item rental model (ADR-103 Amendment-2 D-3 + SCOPE-LOCK Q-3.3)
//
// A `BookingSchema` fent SINGLE-item (1 equipmentType/rental) — a portal egyszerű
// foglalás-flow-ja használja. A V2 bérlés-varázsló (rental-multi-machine mockup-trail)
// ELLENBEN multi-machine: egy bérlés több gépet visz, GÉPENKÉNT eltérő tarifával és
// időszakkal (Q-3.3: "egyik gépet 1 napra, az aggregátort egy hétre"). Ezért a
// per-item `dailyRate` + `tariff` + saját dátum a `RentalItemSchema`-ban él, NEM
// rental-szinten. A `RentalSchema` az aggregate (a wizard "kosár").
//
// Additív (NEM destruktív): a Booking* schemák érintetlenek; a V2 wizard a Rental*
// modellre migrál (Peti frontend-lane), a portal-simple booking maradhat Booking*-on.
// ──────────────────────────────────────────────────────────────────────────

/** Per-gép tarifa-típus — az árazás alapegysége (SCOPE-LOCK Q-3.3, gépenként választható). */
export const RentalTariffSchema = z.enum(['DAILY', 'WEEKLY', 'MONTHLY']);
export type RentalTariff = z.infer<typeof RentalTariffSchema>;

/**
 * Egy bérlés-tétel = egy gép a kosárban. Saját időszak + tarifa + napi díj.
 * `dailyRate` a normalizált napi alap (a `tariff` csak az árazás-megjelenítést/
 * kalkulációt vezérli); a `lineTotal` a tétel végösszege.
 */
export const RentalItemSchema = z
  .object({
    id: z.string().uuid(),
    rentalId: z.string().uuid(),
    equipmentTypeId: z.string().uuid(),
    equipmentUnitId: z.string().uuid().nullable(),
    startDate: z.string().date(),
    endDate: z.string().date(),
    days: z.number().int().positive(),
    tariff: RentalTariffSchema,
    dailyRate: MoneyHufSchema,
    lineTotal: MoneyHufSchema,
    notes: z.string().nullable(),
  })
  .merge(AuditFieldsSchema);
export type RentalItem = z.infer<typeof RentalItemSchema>;

/** Tétel-létrehozás DTO (a szerver számolja a days/dailyRate/lineTotal-t). */
export const RentalItemCreateSchema = RentalItemSchema.pick({
  equipmentTypeId: true,
  equipmentUnitId: true,
  startDate: true,
  endDate: true,
  tariff: true,
});
export type RentalItemCreate = z.infer<typeof RentalItemCreateSchema>;

/** Multi-item bérlés aggregate (V2 wizard kosár). `items` >= 1. */
export const RentalSchema = z
  .object({
    id: z.string().uuid(),
    tenantId: TenantIdSchema,
    portalUserId: z.string().uuid().nullable(),
    partnerId: z.string().uuid().nullable(),
    status: RentalStatusSchema,
    items: z.array(RentalItemSchema).min(1),
    estimatedTotal: MoneyHufSchema,
    depositRequired: MoneyHufSchema.nullable(),
    notes: z.string().nullable(),
  })
  .merge(AuditFieldsSchema);
export type Rental = z.infer<typeof RentalSchema>;

/** Bérlés-létrehozás DTO — partner + tétel-lista (a szerver számol árat/kauciót). */
export const RentalCreateSchema = z.object({
  partnerId: z.string().uuid().nullable().optional(),
  items: z.array(RentalItemCreateSchema).min(1),
  notes: z.string().max(1000).nullable().optional(),
});
export type RentalCreate = z.infer<typeof RentalCreateSchema>;
